import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useKnowledgeStore } from '../store/knowledgeStore'
import { Plus, Search, Eye, ThumbsUp, ThumbsDown, Trash2 } from 'lucide-react'

export default function KnowledgeBasePage() {
  const navigate = useNavigate()
  const documents = useKnowledgeStore((state) => state.documents)
  const searchDocuments = useKnowledgeStore((state) => state.searchDocuments)
  const getCategories = useKnowledgeStore((state) => state.getCategories)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')

  const handleNewArticle = () => {
    navigate('/knowledge-base/new')
  }

  const filteredDocs = searchQuery || selectedCategory !== 'all'
    ? documents.filter(doc => {
        const matchesQuery = !searchQuery ||
          doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          doc.content.toLowerCase().includes(searchQuery.toLowerCase())
        const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory
        return matchesQuery && matchesCategory
      })
    : documents

  const categories = getCategories()

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Knowledge Base</h1>
          <p className="text-blue-200 mt-1">Manage support articles and documentation</p>
        </div>
        <button
          onClick={handleNewArticle}
          className="btn-primary flex items-center space-x-2 cursor-pointer">
          <Plus size={20} />
          <span>New Article</span>
        </button>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="card-body space-y-4">
          <div className="flex space-x-4 items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-300" size={20} />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-base pl-10 w-full"
              />
            </div>
          </div>

          {/* Categories */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-cyber-600 text-white'
                  : 'bg-slate-700/20 text-blue-200 hover:bg-gray-200'
              }`}
            >
              All Articles
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors capitalize ${
                  selectedCategory === cat
                    ? 'bg-cyber-600 text-white'
                    : 'bg-slate-700/20 text-blue-200 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Articles Grid */}
      {filteredDocs.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDocs.map(doc => (
            <Link
              key={doc.id}
              to={`/knowledge-base/${doc.id}`}
              className="card group hover:shadow-lg transition-all"
            >
              <div className="card-body h-full flex flex-col">
                <div className="flex items-start justify-between mb-3">
                  <span className="text-xs bg-slate-700/20 px-2 py-1 rounded capitalize text-blue-200 font-medium">
                    {doc.category}
                  </span>
                  <span className="text-xs text-blue-300">{new Date(doc.updatedAt).toLocaleDateString()}</span>
                </div>
                <h3 className="font-semibold text-white group-hover:text-cyber-700 line-clamp-2 mb-2">
                  {doc.title}
                </h3>
                <p className="text-sm text-blue-200 line-clamp-3 flex-1 mb-4">{doc.content}</p>
                <div className="flex items-center justify-between pt-4 border-t border-blue-500/30">
                  <div className="flex items-center space-x-3 text-xs text-blue-300/70">
                    <div className="flex items-center space-x-1">
                      <Eye size={16} />
                      <span>{doc.views}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <ThumbsUp size={16} />
                      <span>{doc.helpful}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-blue-300/70">No articles found</p>
        </div>
      )}
    </div>
  )
}
